import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ── Auth Store ──────────────────────────────────────────────────────────────
export const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      token: null,
      setAuth: (user, token) => {
        localStorage.setItem('token', token);
        set({ user, token });
      },
      logout: () => {
        localStorage.removeItem('token');
        set({ user: null, token: null });
      },
      isAdmin: () => false, // overridden by selector
    }),
    { name: 'auth-store', partialize: s => ({ user: s.user, token: s.token }) }
  )
);

// ── Cart Store ──────────────────────────────────────────────────────────────
export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [], // { product_id, name, price, qty, image_url, sku, kit_id? }

      addItem: (product, qty = 1, kitId = null, color = null) => {
        const items = get().items;
        const colorKey = color ? `-${color}` : '';
        const key = kitId ? `kit-${kitId}-${product.id || product.product_id}${colorKey}` : `prod-${product.id || product.product_id}${colorKey}`;
        const existing = items.find(i => i._key === key);
        if (existing) {
          set({ items: items.map(i => i._key === key ? { ...i, qty: i.qty + qty } : i) });
        } else {
          set({
            items: [...items, {
              _key: key,
              product_id: product.id || product.product_id,
              kit_id: kitId,
              name: product.name,
              price: product.price,
              qty,
              image_url: product.image_url,
              image_data: product.image_data || null,
              sku: product.sku,
              color: color || null,
            }]
          });
        }
      },

      updateQty: (key, qty) => {
        if (qty <= 0) {
          set({ items: get().items.filter(i => i._key !== key) });
        } else {
          set({ items: get().items.map(i => i._key === key ? { ...i, qty } : i) });
        }
      },

      removeItem: (key) => set({ items: get().items.filter(i => i._key !== key) }),

      clearCart: () => set({ items: [] }),

      // Add entire kit with custom quantities and selected colors
      addKit: (kit, customQtys, selectedColors = {}) => {
        const newItems = kit.items
          .filter(item => (customQtys[item.product_id] ?? item.default_qty) > 0)
          .map(item => {
            const colors = selectedColors[item.product_id] || [];
            return {
              _key: `kit-${kit.id}-${item.product_id}-${colors.join('+')}`,
              product_id: item.product_id,
              kit_id: kit.id,
              name: item.name,
              price: item.price,
              qty: customQtys[item.product_id] ?? item.default_qty,
              image_url: item.image_url,
              image_data: item.image_data || null,
              sku: item.sku,
              colors,
            };
          });

        const existing = get().items.filter(i => i.kit_id !== kit.id);
        set({ items: [...existing, ...newItems] });
      },

      // Add kit as a single bundle at the fixed full kit price
      addFullKit: (kit, selectedColors = {}) => {
        const key = `fullkit-${kit.id}`;
        const bundleItem = {
          _key: key,
          product_id: null,
          kit_id: kit.id,
          kit_bundle: true,
          name: `${kit.name} (Full Kit)`,
          price: parseFloat(kit.full_kit_price),
          qty: 1,
          image_url: null,
          sku: `KIT-${String(kit.id).padStart(4, '0')}`,
          selectedColors,
          kit_items: kit.items,
        };
        const existing = get().items.filter(i => i._key !== key);
        set({ items: [...existing, bundleItem] });
      },

      // Add Custom Kit Bundle (AI generated)
      addCustomKit: (customKit) => {
        const key = customKit.id;
        const bundleItem = {
          _key: key,
          product_id: null,
          kit_id: null,
          isCustomKit: true,
          name: customKit.name,
          price: customKit.totalPrice, // we will use price field so getTotal works natively
          totalPrice: customKit.totalPrice,
          qty: 1,
          studentName: customKit.studentName,
          grade: customKit.grade,
          items: customKit.items
        };
        set({ items: [...get().items, bundleItem] });
      },

      getTotal: () => get().items.reduce((sum, i) => {
        if (i.isCustomKit) return sum + (i.totalPrice * (i.qty || 1));
        return sum + (i.price * i.qty);
      }, 0),
      getCount: () => get().items.reduce((sum, i) => sum + (i.qty || 1), 0),
    }),
    { name: 'cart-store' }
  )
);
